import moment, { Moment } from 'moment'

import { GetterTree } from 'vuex'

import { Invoice, Order, OrderType } from '@/models'

import { orderByDateField, groupByDay } from '@/utils'

import { RootState } from '../../state'
import { OrderingState } from '../state'

const getters: GetterTree<OrderingState, RootState> = {
  /**
   * Returns an Order by its id, or null if not found.
   *
   * @param id Order id to be found.
   */
  orderById: (state) => (id: number): Order | null => {
    return state.orders.elements.find((i) => i.id === id) || null
  },

  /**
   * Returns Orders in an Invoice.
   *
   * @param id Invoice id to search in Orders.
   */
  ordersByInvoiceId: (state) => (id: number): Order[] => {
    return state.orders.elements.filter((i) => i.invoiceId === id) || []
  },

  /**
   * Returns the last time an Order was updated.
   *
   * @param order Order whose last time will be returned.
   */
  orderLastUpdate: () => (order: Order): Date => {
    return (
      order.canceledAt ||
      order.completedAt ||
      order.startedAt ||
      order.authorizedAt ||
      order.createdAt
    )
  },

  /**
   * Returns a list of location names for display.
   */
  orderLocations: (_, getters) => (order: Order): string[] | null => {
    if (order.type === OrderType.DELIVERY) {
      return ['Delivery']
    }

    if (order.type === OrderType.TAKEOUT_LATER) {
      return ['Retirada']
    }

    const invoice: Invoice | null = getters.invoiceById(order.invoiceId)

    if (invoice === null) {
      return null
    }

    const result = [invoice.location]

    if (order.type === OrderType.TAKEOUT_NOW) {
      result.push('Para levar')
    }

    return result
  },

  /**
   * Returns Orders filtered with current query.
   *
   * It must replicate the API filtering to avoid mismatching the total
   * and reloading the pages infinitely.
   */
  filteredOrders (state): Order[] {
    const query = state.orders.query

    const fromDate = query.from ? moment.utc(query.from).startOf('day') : null
    const untilDate = query.until ? moment.utc(query.until).endOf('day') : null

    return state.orders.elements.filter((order) => (
      (!fromDate || fromDate.isSameOrBefore(order.createdAt)) &&
      (!untilDate || untilDate.isSameOrAfter(order.createdAt)) &&
      (!query.status || query.status.includes(order.status))
    ))
  },

  /**
   * Returns Orders ordered by authorizedAt.
   *
   * Orders with OPEN status are placed last.
   */
  orderedOrders (_, getters): Order[] {
    return orderByDateField(getters.filteredOrders, 'authorizedAt')
  },

  /**
   * Returns Orders grouped by their `createdAt`.
   *
   * The groups are in descending order.
   */
  orderGroupsByDay (_, getters): [Moment, Order[]][] {
    return groupByDay(getters.orderedOrders, 'createdAt')
  }
}

export default getters
